import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCw, ShieldCheck, ShieldOff } from "lucide-react";
import api, { formatApiError } from "../../lib/api";

const FILTRES = [
  { id: "tous", label: "Tous" },
  { id: "cotisations", label: "Cotisations" },
  { id: "licences", label: "Licences" },
  { id: "ecole", label: "École de vélo" },
  { id: "mecenat", label: "Mécénat" },
  { id: "sponsoring", label: "Sponsoring" },
  { id: "autres", label: "Autres" },
];

const TYPE_LABELS = {
  cotisations: "Cotisation",
  licences: "Licence",
  ecole: "École de vélo",
  mecenat: "Mécénat",
  sponsoring: "Sponsoring (contact)",
  autres: "Autre",
};

const TYPE_COLORS = {
  cotisations: "bg-navy/10 text-navy",
  licences: "bg-electrik/15 text-electrik",
  ecole: "bg-rouge/10 text-rouge",
  mecenat: "bg-emerald-100 text-emerald-800",
  sponsoring: "bg-amber-100 text-amber-800",
  autres: "bg-steel/15 text-steel",
};

export default function AdminPaiements() {
  const [filtre, setFiltre] = useState("tous");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (f = filtre) => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/paiements", { params: { filtre: f } });
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [filtre]);

  useEffect(() => {
    load(filtre);
  }, [filtre, load]);

  const items = data?.items || [];

  return (
    <main className="max-w-[1400px] mx-auto px-5 sm:px-8 py-10" data-testid="admin-paiements">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="font-heading uppercase font-semibold tracking-tight text-navy text-2xl">Paiements</h2>
          <p className="text-steel text-sm mt-1">
            Adhésions, licences, école de vélo, mécénat et demandes de partenariat — sans mélange.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span
            data-testid="recus-fiscaux-etat"
            className={`inline-flex items-center gap-2 text-xs font-heading uppercase tracking-wider px-4 py-2 border ${
              data?.recus_fiscaux_actifs ? "border-emerald-500 text-emerald-700" : "border-navy/20 text-steel"
            }`}
          >
            {data?.recus_fiscaux_actifs ? <ShieldCheck size={14} /> : <ShieldOff size={14} />}
            Reçus fiscaux : {data?.recus_fiscaux_actifs ? "habilités" : "désactivés"}
          </span>
          <button
            onClick={() => load()}
            data-testid="paiements-refresh"
            className="inline-flex items-center gap-2 border border-navy/20 text-navy px-4 py-2 text-xs font-heading uppercase tracking-wider hover:border-rouge hover:text-rouge transition-colors"
          >
            <RefreshCw size={14} /> Actualiser
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="paiements-filtres">
        {FILTRES.map((f) => (
          <button
            key={f.id}
            onClick={() => setFiltre(f.id)}
            data-testid={`filtre-${f.id}`}
            className={`font-heading uppercase tracking-wider text-xs px-5 py-2.5 border transition-colors duration-300 ${
              filtre === f.id ? "bg-rouge border-rouge text-white" : "border-navy/20 text-navy/70 hover:border-rouge hover:text-rouge"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center gap-3 text-steel py-16 justify-center" data-testid="paiements-loading">
          <Loader2 size={20} className="animate-spin" /> Chargement…
        </div>
      ) : items.length === 0 ? (
        <p className="text-steel text-sm py-16 text-center" data-testid="paiements-empty">Aucun élément dans ce filtre.</p>
      ) : (
        <div className="overflow-x-auto bg-white border border-navy/10" data-testid="paiements-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-navy/10 text-left">
                <th className="px-4 py-3 font-heading uppercase tracking-wider text-xs text-steel">Type</th>
                <th className="px-4 py-3 font-heading uppercase tracking-wider text-xs text-steel">Nom / Entreprise</th>
                <th className="px-4 py-3 font-heading uppercase tracking-wider text-xs text-steel">Montant</th>
                <th className="px-4 py-3 font-heading uppercase tracking-wider text-xs text-steel">Date</th>
                <th className="px-4 py-3 font-heading uppercase tracking-wider text-xs text-steel">Statut</th>
                <th className="px-4 py-3 font-heading uppercase tracking-wider text-xs text-steel">Webhook</th>
                <th className="px-4 py-3 font-heading uppercase tracking-wider text-xs text-steel">Transaction Stripe</th>
                <th className="px-4 py-3 font-heading uppercase tracking-wider text-xs text-steel">Références</th>
                <th className="px-4 py-3 font-heading uppercase tracking-wider text-xs text-steel">Reçu fiscal</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={`${p.source}-${p.id}`} className="border-b border-navy/5 last:border-0 hover:bg-mist/60" data-testid={`paiement-${p.id}`}>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2.5 py-1 text-[11px] font-heading uppercase tracking-wider ${TYPE_COLORS[p.type_paiement] || TYPE_COLORS.autres}`} data-testid={`paiement-type-${p.id}`}>
                      {TYPE_LABELS[p.type_paiement] || p.type_paiement}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-semibold text-navy">{p.prenom} {p.nom}</p>
                    {p.entreprise && <p className="text-xs text-steel">{p.entreprise}</p>}
                    {p.formule && <p className="text-xs text-steel">{p.formule}</p>}
                  </td>
                  <td className="px-4 py-3 font-semibold text-navy whitespace-nowrap">{p.montant}</td>
                  <td className="px-4 py-3 text-steel whitespace-nowrap">{(p.date || "").slice(0, 10)}</td>
                  <td className="px-4 py-3">
                    <p className="text-xs text-navy">{p.statut}</p>
                    <p className="text-[11px] text-steel">{p.payment_status}</p>
                  </td>
                  <td className="px-4 py-3 text-xs text-steel">{p.webhook}</td>
                  <td className="px-4 py-3 text-[11px] text-steel font-mono break-all max-w-40">{p.session_id || "—"}</td>
                  <td className="px-4 py-3 text-[11px] text-steel font-mono break-all max-w-40">
                    {p.member_id && <p>membre : {p.member_id.slice(0, 8)}…</p>}
                    {p.registration_id && <p>dossier : {p.registration_id.slice(0, 8)}…</p>}
                    {!p.member_id && !p.registration_id && "—"}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {p.type_paiement === "mecenat" ? (
                      <span className="text-steel" data-testid={`recu-${p.id}`}>
                        {p.recu_statut === "emis" ? `Émis (${p.recu_numero})` : "Non émis"}
                      </span>
                    ) : (
                      <span className="text-steel/50">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </main>
  );
}
